import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Save,
  Building,
  Tag,
  DollarSign,
  Calendar,
  CreditCard,
  FileText,
  Upload,
  AlertCircle,
  Repeat
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';

const ExpenseFormPage = () => {
  const navigate = useNavigate();
  const { expenseId } = useParams();
  const isEditMode = Boolean(expenseId);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vendor: '',
    category: 'legal',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    invoiceId: '',
    caseId: '',
    status: 'paid',
    recurring: false,
    attachments: null,
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [attachmentRequired, setAttachmentRequired] = useState(false);

  // Load expense data if editing
  useEffect(() => {
    if (isEditMode) {
      // Mock data - سيتم استبداله بـ API
      setTimeout(() => {
        const mockExpense = {
          vendor: 'شركة المحاماة الاستشارية',
          category: 'legal',
          amount: '8000',
          date: '2024-12-01',
          paymentMethod: 'bank_transfer',
          invoiceId: 'INV-VENDOR-001',
          caseId: 'WZ-2024-001',
          status: 'paid',
          recurring: false,
          attachments: null,
          notes: 'مصروف من استشارة قانونية'
        };
        setFormData(mockExpense);
      }, 300);
    }
  }, [isEditMode, expenseId]);

  // Check if attachment is required based on amount
  useEffect(() => {
    const amount = parseFloat(formData.amount);
    setAttachmentRequired(amount > 10000);
  }, [formData.amount]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFormData(prev => ({ ...prev, attachments: files }));
      if (errors.attachments) {
        setErrors(prev => ({ ...prev, attachments: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendor.trim()) {
      newErrors.vendor = 'اسم الجهة مطلوب';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'المبلغ مطلوب ويجب أن يكون أكبر من صفر';
    }

    if (!formData.date) {
      newErrors.date = 'تاريخ المصروف مطلوب';
    }

    // Attachment required if amount > 10000
    if (attachmentRequired && !formData.attachments && !isEditMode) {
      newErrors.attachments = 'المرفقات إلزامية للمبالغ التي تتجاوز 10,000 ر.س';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Mock API call
    setTimeout(() => {
      console.log('Form Data:', formData);
      alert(isEditMode ? 'تم تحديث المصروف بنجاح' : 'تم إضافة المصروف بنجاح');
      navigate('/admin/finance/expenses');
      setLoading(false);
    }, 1000);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/expenses')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للمصروفات</span>
          </button>

          <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">
            {isEditMode ? 'تعديل مصروف' : 'إضافة مصروف جديد'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'تحديث معلومات المصروف' : 'أدخل بيانات المصروف الجديد'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Vendor Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-[#1E3A8A]" />
              بيانات الجهة
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vendor Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  اسم الجهة المسددة لها <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    errors.vendor
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="مثال: شركة المحاماة الاستشارية"
                />
                {errors.vendor && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.vendor}
                  </p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  التصنيف <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Tag className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
                  >
                    <option value="legal">قانوني</option>
                    <option value="administrative">إداري</option>
                    <option value="technical">تقني</option>
                    <option value="marketing">تسويق</option>
                    <option value="operational">تشغيلي</option>
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الحالة
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="paid">مدفوع</option>
                  <option value="under_review">تحت المراجعة</option>
                  <option value="cancelled">ملغي</option>
                </select>
              </div>
            </div>
          </div>

          {/* Expense Details */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#1E3A8A]" />
              تفاصيل المصروف
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  المبلغ (ر.س) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.amount
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                {errors.amount && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.amount}
                  </p>
                )}
                {attachmentRequired && (
                  <p className="mt-1 text-xs text-yellow-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    المرفقات إلزامية لهذا المبلغ
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  تاريخ المصروف <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.date
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                  />
                </div>
                {errors.date && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.date}
                  </p>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  طريقة الدفع
                </label>
                <div className="relative">
                  <CreditCard className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
                  >
                    <option value="cash">نقداً</option>
                    <option value="credit_card">بطاقة ائتمانية</option>
                    <option value="bank_transfer">تحويل بنكي</option>
                  </select>
                </div>
              </div>

              {/* Recurring */}
              <div className="flex items-center gap-3 pt-8">
                <input
                  type="checkbox"
                  name="recurring"
                  checked={formData.recurring}
                  onChange={handleChange}
                  className="w-5 h-5 text-[#1E3A8A] rounded focus:ring-2 focus:ring-[#1E3A8A]"
                />
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <Repeat className="w-4 h-4" />
                  مصروف متكرر
                </label>
              </div>

              {/* Invoice ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم الفاتورة (اختياري)
                </label>
                <div className="relative">
                  <FileText className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="invoiceId"
                    value={formData.invoiceId}
                    onChange={handleChange}
                    className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    placeholder="INV-VENDOR-001"
                  />
                </div>
              </div>

              {/* Case ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم القضية (اختياري)
                </label>
                <input
                  type="text"
                  name="caseId"
                  value={formData.caseId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="WZ-2024-001"
                />
              </div>

              {/* Attachments */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  المرفقات {attachmentRequired && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 cursor-pointer transition-all">
                    <Upload className="w-4 h-4" />
                    <span>رفع المرفقات</span>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                  </label>
                  {formData.attachments && (
                    <span className="text-sm text-gray-600">
                      {formData.attachments.length} ملف محدد
                    </span>
                  )}
                </div>
                {errors.attachments && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.attachments}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  صيغ مدعومة: PDF, DOC, DOCX, JPG, PNG | الحد الأقصى: 10 MB لكل ملف
                </p>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  ملاحظات (اختياري)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  rows="3"
                  placeholder="أي ملاحظات إضافية..."
                ></textarea>
              </div>
            </div>
          </div>

          {/* Warning for attachments */}
          {attachmentRequired && !formData.attachments && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-700">
                <p className="font-bold mb-1">تنبيه:</p>
                <p>المرفقات إلزامية للمبالغ التي تتجاوز 10,000 ر.س. يرجى رفع الفواتير أو المستندات الداعمة.</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>جاري الحفظ...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{isEditMode ? 'حفظ التعديلات' : 'إضافة المصروف'}</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/finance/expenses')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ExpenseFormPage;
